/**
 * Relativity - 狭义相对论物理计算类
 * @description 实现狭义相对论的核心公式：洛伦兹因子、时间膨胀、长度收缩、速度叠加、多普勒效应
 */

/**
 * 光速常量（游戏简化单位）
 * 真实物理: c = 299,792,458 m/s
 * 游戏简化: c = 1（便于计算和理解相对论效应）
 */
export const SPEED_OF_LIGHT = 1.0;

/**
 * 真实光速（用于显示和教学）
 */
export const SPEED_OF_LIGHT_REAL = 299792458; // m/s

/**
 * 速度比率阈值（防止接近光速时的数值问题）
 */
export const VELOCITY_RATIO_THRESHOLD = 0.9999;

/**
 * 相对论计算结果
 */
export interface RelativityResult {
  /** 计算值 */
  value: number;
  /** 洛伦兹因子（用于参考） */
  gamma: number;
  /** 速度与光速的比率 */
  velocityRatio: number;
}

/**
 * 相对论速度叠加结果
 */
export interface VelocityAdditionResult {
  /** 合成速度 */
  resultantVelocity: number;
  /** 合成速度与光速的比率 */
  resultantRatio: number;
  /** 是否接近光速 */
  nearLightSpeed: boolean;
}

/**
 * 多普勒效应结果
 */
export interface DopplerResult {
  /** 多普勒因子 */
  factor: number;
  /** 频率变化比率 */
  frequencyRatio: number;
  /** 波长变化比率 */
  wavelengthRatio: number;
  /** 观测者视角描述 */
  description: string;
}

/**
 * 相对论变换参数
 */
export interface RelativityTransform {
  /** 参考系速度（相对于静止参考系） */
  frameVelocity: number;
  /** 时间间隔（静止参考系中的时间） */
  properTime?: number;
  /** 长度（静止参考系中的长度） */
  properLength?: number;
}

/**
 * Relativity 类
 * 提供狭义相对论的核心计算功能
 */
export class Relativity {
  private lightSpeed: number;

  /**
   * 创建相对论计算实例
   * @param customLightSpeed 自定义光速值（默认使用游戏简化单位 1.0）
   */
  constructor(customLightSpeed?: number) {
    this.lightSpeed = customLightSpeed ?? SPEED_OF_LIGHT;
  }

  /**
   * 获取光速值
   */
  getLightSpeed(): number {
    return this.lightSpeed;
  }

  /**
   * 设置光速值
   * @param speed 新的光速值
   */
  setLightSpeed(speed: number): void {
    this.lightSpeed = speed;
  }

  /**
   * 计算速度与光速的比率
   * @param velocity 速度值
   * @returns 速度比率 v/c
   */
  calculateVelocityRatio(velocity: number): number {
    return Math.abs(velocity) / this.lightSpeed;
  }

  /**
   * 验证速度是否有效（不超过光速）
   * @param velocity 速度值
   * @returns 是否有效
   */
  isValidVelocity(velocity: number): boolean {
    const ratio = this.calculateVelocityRatio(velocity);
    return ratio < 1 && ratio >= 0;
  }

  /**
   * 计算洛伦兹因子
   * 公式: γ = 1/√(1 - v²/c²)
   * @param velocity 速度值
   * @returns 洛伦兹因子 γ
   * @throws Error 如果速度超过光速
   */
  calculateGamma(velocity: number): number {
    const ratio = this.calculateVelocityRatio(velocity);

    // 验证速度不超过光速
    if (ratio >= 1) {
      throw new Error(`速度 ${velocity} 超过光速 ${this.lightSpeed}，洛伦兹因子无定义`);
    }

    // 防止接近光速时的数值问题
    const cappedRatio = Math.min(ratio, VELOCITY_RATIO_THRESHOLD);

    // γ = 1/√(1 - v²/c²)
    const gamma = 1 / Math.sqrt(1 - cappedRatio * cappedRatio);

    return gamma;
  }

  /**
   * 计算时间膨胀
   * 公式: t' = γt（运动参考系中的时间比静止参考系中的时间更长）
   * @param properTime 本征时间（静止参考系中的时间）
   * @param velocity 参考系速度
   * @returns 时间膨胀结果
   */
  timeDilation(properTime: number, velocity: number): RelativityResult {
    const gamma = this.calculateGamma(velocity);
    const ratio = this.calculateVelocityRatio(velocity);

    // t' = γt（运动物体经历的时间更长）
    const dilatedTime = gamma * properTime;

    return {
      value: dilatedTime,
      gamma,
      velocityRatio: ratio,
    };
  }

  /**
   * 计算长度收缩
   * 公式: L' = L/γ（运动方向上的长度缩短）
   * @param properLength 本征长度（静止参考系中的长度）
   * @param velocity 参考系速度
   * @returns 长度收缩结果
   */
  lengthContraction(properLength: number, velocity: number): RelativityResult {
    const gamma = this.calculateGamma(velocity);
    const ratio = this.calculateVelocityRatio(velocity);

    // L' = L/γ（运动物体在运动方向上缩短）
    const contractedLength = properLength / gamma;

    return {
      value: contractedLength,
      gamma,
      velocityRatio: ratio,
    };
  }

  /**
   * 相对论速度叠加
   * 公式: v = (v₁ + v₂) / (1 + v₁v₂/c²)
   * 用于计算两个参考系中物体速度的合成
   * @param v1 第一个速度（参考系1相对于参考系0的速度）
   * @param v2 第二个速度（物体相对于参考系1的速度）
   * @returns 速度叠加结果
   */
  velocityAddition(v1: number, v2: number): VelocityAdditionResult {
    // 验证两个速度都有效
    if (!this.isValidVelocity(v1) || !this.isValidVelocity(v2)) {
      throw new Error('速度叠加中的速度不能超过光速');
    }

    const c = this.lightSpeed;
    const cSquared = c * c;

    // 相对论速度叠加公式: v = (v₁ + v₂) / (1 + v₁v₂/c²)
    const numerator = v1 + v2;
    const denominator = 1 + (v1 * v2) / cSquared;
    const resultantVelocity = numerator / denominator;

    // 确保结果不超过光速
    const cappedVelocity = Math.min(Math.abs(resultantVelocity), c * VELOCITY_RATIO_THRESHOLD);
    const finalVelocity = resultantVelocity >= 0 ? cappedVelocity : -cappedVelocity;

    const resultantRatio = Math.abs(finalVelocity) / c;
    const nearLightSpeed = resultantRatio > 0.9;

    return {
      resultantVelocity: finalVelocity,
      resultantRatio,
      nearLightSpeed,
    };
  }

  /**
   * 计算多普勒因子
   * 公式: D = √((1 + β)/(1 - β))（接近时）或 D = √((1 - β)/(1 + β))（远离时）
   * 其中 β = v/c
   * @param velocity 相对速度
   * @param isApproaching 是否接近（true=接近，false=远离）
   * @returns 多普勒效应结果
   */
  dopplerEffect(velocity: number, isApproaching: boolean): DopplerResult {
    const ratio = this.calculateVelocityRatio(velocity);

    // 验证速度有效
    if (!this.isValidVelocity(velocity)) {
      throw new Error('多普勒效应计算中的速度不能超过光速');
    }

    // 防止接近光速时的数值问题
    const cappedRatio = Math.min(ratio, VELOCITY_RATIO_THRESHOLD);

    // 多普勒因子公式
    // 接近时: D = √((1 + β)/(1 - β))，频率增加（蓝移）
    // 远离时: D = √((1 - β)/(1 + β))，频率减少（红移）
    let factor: number;
    if (isApproaching) {
      factor = Math.sqrt((1 + cappedRatio) / (1 - cappedRatio));
    } else {
      factor = Math.sqrt((1 - cappedRatio) / (1 + cappedRatio));
    }

    // 频率比率 = D（接近时 >1，远离时 <1）
    const frequencyRatio = factor;

    // 波长比率 = 1/D（接近时 <1，远离时 >1）
    const wavelengthRatio = 1 / factor;

    // 生成描述
    let description: string;
    if (isApproaching) {
      if (ratio > 0.5) {
        description = `高速接近（${ratio.toFixed(2)}c），强烈蓝移效应`;
      } else {
        description = `低速接近（${ratio.toFixed(2)}c），轻微蓝移效应`;
      }
    } else {
      if (ratio > 0.5) {
        description = `高速远离（${ratio.toFixed(2)}c），强烈红移效应`;
      } else {
        description = `低速远离（${ratio.toFixed(2)}c），轻微红移效应`;
      }
    }

    return {
      factor,
      frequencyRatio,
      wavelengthRatio,
      description,
    };
  }

  /**
   * 计算相对论动能
   * 公式: E_k = (γ - 1)mc²
   * @param mass 物体质量
   * @param velocity 物体速度
   * @returns 相对论动能
   */
  relativisticKineticEnergy(mass: number, velocity: number): number {
    const gamma = this.calculateGamma(velocity);
    const cSquared = this.lightSpeed * this.lightSpeed;

    // E_k = (γ - 1)mc²
    return (gamma - 1) * mass * cSquared;
  }

  /**
   * 计算相对论总能量
   * 公式: E = γmc²
   * @param mass 物体静止质量
   * @param velocity 物体速度
   * @returns 相对论总能量
   */
  relativisticTotalEnergy(mass: number, velocity: number): number {
    const gamma = this.calculateGamma(velocity);
    const cSquared = this.lightSpeed * this.lightSpeed;

    // E = γmc²
    return gamma * mass * cSquared;
  }

  /**
   * 计算静止能量
   * 公式: E_0 = mc²
   * @param mass 物体静止质量
   * @returns 静止能量
   */
  restEnergy(mass: number): number {
    return mass * this.lightSpeed * this.lightSpeed;
  }

  /**
   * 计算相对论动量
   * 公式: p = γmv
   * @param mass 物体质量
   * @param velocity 物体速度
   * @returns 相对论动量
   */
  relativisticMomentum(mass: number, velocity: number): number {
    const gamma = this.calculateGamma(velocity);

    // p = γmv
    return gamma * mass * velocity;
  }

  /**
   * 批量计算相对论效应
   * 用于游戏关卡中一次性展示所有相对论效应
   * @param transform 变换参数
   * @returns 包含所有效应的结果
   */
  calculateAllEffects(transform: RelativityTransform): {
    gamma: number;
    velocityRatio: number;
    timeDilation: RelativityResult | null;
    lengthContraction: RelativityResult | null;
    dopplerApproaching: DopplerResult;
    dopplerReceding: DopplerResult;
  } {
    const gamma = this.calculateGamma(transform.frameVelocity);
    const velocityRatio = this.calculateVelocityRatio(transform.frameVelocity);

    // 时间膨胀（如果提供了本征时间）
    const timeDilationResult = transform.properTime !== undefined
      ? this.timeDilation(transform.properTime, transform.frameVelocity)
      : null;

    // 长度收缩（如果提供了本征长度）
    const lengthContractionResult = transform.properLength !== undefined
      ? this.lengthContraction(transform.properLength, transform.frameVelocity)
      : null;

    // 多普勒效应（接近和远离两种情况）
    const dopplerApproaching = this.dopplerEffect(transform.frameVelocity, true);
    const dopplerReceding = this.dopplerEffect(transform.frameVelocity, false);

    return {
      gamma,
      velocityRatio,
      timeDilation: timeDilationResult,
      lengthContraction: lengthContractionResult,
      dopplerApproaching,
      dopplerReceding,
    };
  }

  /**
   * 将速度转换为游戏显示格式
   * @param velocity 速度值
   * @returns 格式化字符串（如 "0.5c"）
   */
  formatVelocity(velocity: number): string {
    const ratio = this.calculateVelocityRatio(velocity);
    return `${ratio.toFixed(2)}c`;
  }

  /**
   * 将洛伦兹因子转换为游戏显示格式
   * @param gamma 洛伦兹因子
   * @returns 格式化字符串
   */
  formatGamma(gamma: number): string {
    if (gamma < 1.01) {
      return '≈1（低速效应可忽略）';
    } else if (gamma < 2) {
      return `${gamma.toFixed(2)}（中等速度）`;
    } else if (gamma < 10) {
      return `${gamma.toFixed(1)}（高速效应显著）`;
    } else {
      return `${gamma.toFixed(0)}（接近光速）`;
    }
  }

  /**
   * 获取速度等级分类（用于关卡难度）
   * @param velocity 速度值
   * @returns 速度等级
   */
  getSpeedLevel(velocity: number): 'low' | 'medium' | 'high' | 'ultra' {
    const ratio = this.calculateVelocityRatio(velocity);

    if (ratio < 0.1) {
      return 'low'; // 低速：经典物理适用
    } else if (ratio < 0.5) {
      return 'medium'; // 中速：轻微相对论效应
    } else if (ratio < 0.9) {
      return 'high'; // 高速：显著相对论效应
    } else {
      return 'ultra'; // 超高速：极端相对论效应
    }
  }
}

